import React from 'react';

import { useIntl } from 'react-intl';
import { useLocation } from 'react-router';

import { ICartEntry } from '@rbi-ctg/menu';
import { FavoriteIcon } from 'components/favorite-icon';
import { ButtonForIconFav } from 'components/favorite-icon/styled';
import { LoadingAnimation } from 'components/lottie/favorite/loading';
import {
  GetUserFavoritesRefsDocument,
  ICartEntryInput,
  IGetUserFavoritesRefsQuery,
  useCreateFavoriteMutation,
  useDeleteFavoriteMutation,
  useGetUserFavoritesRefsQuery,
} from 'generated/rbi-graphql';
import { useCdpContext } from 'state/cdp';
import { CustomEventNames, EventTypes } from 'state/cdp/constants';
import { isOfferType } from 'state/order/utils';
import { useStoreContext } from 'state/store';
import { remappedCartForBackEnd } from 'utils/cart';
import { getCartEntryRef } from 'utils/menu/get-cart-entry-ref';
import { routes } from 'utils/routing';
import { toast } from 'utils/toast';

interface IFavoriteEntryButtonProps {
  cartEntry: ICartEntry;
}

export const FavoriteEntryButton: React.FC<IFavoriteEntryButtonProps> = ({ cartEntry }) => {
  const { data } = useGetUserFavoritesRefsQuery();
  const [createFavorite, { loading: createLoading }] = useCreateFavoriteMutation();
  const [deleteFavorite, { loading: deleteLoading }] = useDeleteFavoriteMutation();

  const ref = getCartEntryRef(cartEntry);
  const refFavorite = data?.userFavorites.favorites?.find(favorite => favorite?.ref === ref);
  const isFavorite = !!refFavorite;
  const { formatMessage } = useIntl();
  const cdp = useCdpContext();
  const location = useLocation();

  const shouldDisplayHeart = !isOfferType(cartEntry.type);
  const { restaurantLogAttributesRef } = useStoreContext();

  const addEvent = (favoriteItemName: string) => {
    const isMenuCurrentPath = location.pathname.startsWith(routes.menu);

    cdp.trackEvent({
      name: CustomEventNames.BUTTON_CLICK,
      type: EventTypes.Other,
      attributes: {
        Name: CustomEventNames.ADD_ITEM_TO_FAVORITES,
        'Source Page': isMenuCurrentPath ? `${routes.menu}/` : location.pathname,
        ...restaurantLogAttributesRef.current,
      },
    });

    cdp.trackEvent({
      name: CustomEventNames.ADD_ITEMS_TO_FAVORITE,
      type: EventTypes.Other,
      attributes: {
        productName: favoriteItemName,
      },
    });
  };

  const onClick = () => {
    if (isFavorite) {
      // Delete favorite
      deleteFavorite({
        variables: {
          favoriteId: refFavorite?._id ?? '',
        },
        update: (cache, { data: resultData }) => {
          const favs = cache.readQuery<IGetUserFavoritesRefsQuery>({
            query: GetUserFavoritesRefsDocument,
          });

          const newQuery = {
            userFavorites: {
              ...favs?.userFavorites,
              count: favs?.userFavorites.count ? favs.userFavorites.count - 1 : 0,
              favorites: favs?.userFavorites.favorites?.filter(
                item => item?._id !== resultData?.deleteFavorite._id
              ),
            },
          };
          cache.writeQuery({
            query: GetUserFavoritesRefsDocument,
            data: newQuery,
          });
        },
      })
        .then(() => {
          toast.success(formatMessage({ id: 'removeFavoriteSucess' }));
        })
        .catch(() => {
          toast.error(formatMessage({ id: 'itemFavoritedError' }));
        });
    } else {
      // Map the entries to ICartEntryInput
      const entries = remappedCartForBackEnd([cartEntry]) as unknown as ICartEntryInput[];
      // Create favorite
      createFavorite({
        variables: {
          input: {
            entries,
            name: cartEntry.name,
            ref,
          },
        },
        update: (cache, { data: resultData }) => {
          const favs = cache.readQuery<IGetUserFavoritesRefsQuery>({
            query: GetUserFavoritesRefsDocument,
          });

          const newQuery = {
            userFavorites: {
              ...favs?.userFavorites,
              count: (favs?.userFavorites.count ?? 0) + 1,
              favorites: [
                {
                  ref: resultData?.createFavorite.ref ?? '',
                  _id: resultData?.createFavorite._id ?? '',
                  __typename: resultData?.createFavorite.__typename,
                },
              ].concat(
                favs?.userFavorites.favorites as ConcatArray<{
                  ref: string;
                  _id: string;
                  __typename: 'Favorite' | undefined;
                }>
              ),
            },
          };
          cache.writeQuery({
            query: GetUserFavoritesRefsDocument,
            data: newQuery,
          });
        },
      })
        .then(() => {
          addEvent(cartEntry.name);
          toast.success(formatMessage({ id: 'itemFavorited' }));
        })
        .catch(() => {
          toast.error(formatMessage({ id: 'itemFavoritedError' }));
        });
    }
  };

  const loading = createLoading || deleteLoading;

  const FavIcon = <FavoriteIcon isFavorite={isFavorite} />;

  return shouldDisplayHeart ? (
    <ButtonForIconFav
      data-testid={isFavorite ? 'fav-icon-selected' : 'fav-icon'}
      onClick={onClick}
      aria-label={formatMessage({
        id: isFavorite ? 'removeRestaurantFromFavorites' : 'setRestaurantAsFavorite',
      })}
      isTransparent={true}
    >
      {loading ? (
        <React.Suspense data-testid="fav_entry_loading_animation" fallback={FavIcon}>
          <LoadingAnimation />
        </React.Suspense>
      ) : (
        FavIcon
      )}
    </ButtonForIconFav>
  ) : null;
};
